import { useEffect, useMemo, useState } from 'react';

import { InvestigationAiRoleLearning } from '../components/InvestigationAiRole';

import {
  loadCanonicalTourFixture,
  type TourCollaborativeInvestigationExample,
  type TourCollaborativeMoment,
  type TourCollaborativeParticipant,
  type TourFixture,
} from '../../product-tour/data/tour-data';

export function InvestigationLearningPage() {
  const [fixture, setFixture] = useState<TourFixture | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void loadCanonicalTourFixture()
      .then((value) => {
        if (active) setFixture(value);
      })
      .catch((reason: unknown) => {
        if (active) setError(reason instanceof Error ? reason.message : 'The Investigation example could not be loaded.');
      });
    return () => {
      active = false;
    };
  }, []);

  if (error) {
    return (
      <section className="r42-page r42-investigation-learn-state" role="alert">
        <i className="bi bi-exclamation-circle" aria-hidden="true" />
        <div>
          <strong>Investigation example unavailable</strong>
          <p>{error}</p>
        </div>
      </section>
    );
  }

  if (!fixture) {
    return (
      <section className="r42-page r42-investigation-learn-state" role="status" aria-live="polite">
        <span className="r42-learning-dot" aria-hidden="true" />
        <div>
          <strong>Opening the Investigation example…</strong>
          <p>Route42 is loading the fictional P-101 learning story.</p>
        </div>
      </section>
    );
  }

  return <InvestigationLearningExperience example={fixture.collaborative_investigation_example} />;
}

export function InvestigationLearningExperience({ example }: { example: TourCollaborativeInvestigationExample }) {
  const participants = useMemo(
    () => new Map(example.participants.map((participant) => [participant.participant_id, participant])),
    [example.participants],
  );

  return (
    <section className="r42-investigation-learn-page" data-canonical-example={example.example_id}>
      <header className="r42-investigation-learn-hero">
        <div className="r42-investigation-learn-hero__copy">
          <span className="r42-eyebrow">Investigations · Guided example</span>
          <h1>See an Investigation in action</h1>
          <p>Follow how three team members and Route42 work through one machine problem from first observation to resolution.</p>
        </div>
        <div className="r42-investigation-learn-problem" aria-label="Example Investigation">
          <span>Example Investigation</span>
          <strong>{example.title}</strong>
          <p>{example.opening_assessment}</p>
        </div>
      </header>

      <section className="r42-investigation-learn-explainers" aria-label="Investigation basics">
        <ExplainerCard
          icon="bi bi-people"
          title="What it is"
          body="One shared place to work through a machine problem."
        />
        <ExplainerCard
          icon="bi bi-signpost-split"
          title="When to use it"
          body="When the issue matters, remains unresolved, or needs input over time."
        />
        <ExplainerCard
          icon="bi bi-stars"
          title="How Route42 helps"
          body="AI connects evidence and keeps the team's understanding organized as new information arrives."
        />
      </section>

      <InvestigationAiRoleLearning />

      <section className="r42-investigation-learn-story" aria-labelledby="r42-investigation-story-title">
        <div className="r42-investigation-learn-story__head">
          <div>
            <span className="r42-eyebrow">One problem · shared over time</span>
            <h2 id="r42-investigation-story-title">How the team's understanding changes</h2>
            <p>Each contribution becomes part of the same evidence trail. Route42 shows what changed and what the team should do next.</p>
          </div>
          <ParticipantLegend participants={example.participants} />
        </div>

        <ol className="r42-investigation-timeline" aria-label="Seven moments in the P-101 Investigation">
          {example.moments.map((moment) => {
            const participant = participants.get(moment.primary_participant_id);
            if (!participant) return null;
            return <InvestigationMomentCard key={moment.moment_id} moment={moment} participant={participant} />;
          })}
        </ol>
      </section>

      <section className="r42-investigation-learn-resolution" aria-labelledby="r42-investigation-resolution-title">
        <div className="r42-investigation-learn-resolution__icon" aria-hidden="true"><i className="bi bi-check2-circle" /></div>
        <div>
          <span className="r42-eyebrow">Investigation resolved</span>
          <h2 id="r42-investigation-resolution-title">The team knows what happened—and the learning remains useful.</h2>
          <p>{example.closing_assessment}</p>
          <div className="r42-investigation-learn-resolution__memory">
            <i className="bi bi-cpu" aria-hidden="true" />
            <span>Route42 preserves the evidence-backed pattern in Machine Memory so a future worker can start from what this team learned.</span>
          </div>
        </div>
      </section>
    </section>
  );
}

function ExplainerCard({ icon, title, body }: { icon: string; title: string; body: string }) {
  return (
    <article className="r42-investigation-learn-explainer">
      <div className="r42-investigation-learn-explainer__icon" aria-hidden="true"><i className={icon} /></div>
      <div>
        <h2>{title}</h2>
        <p>{body}</p>
      </div>
    </article>
  );
}

function ParticipantLegend({ participants }: { participants: TourCollaborativeParticipant[] }) {
  return (
    <div className="r42-investigation-learn-people" aria-label="Participants">
      {participants.map((participant) => (
        <span key={participant.participant_id} title={`${participant.name} · ${participant.role}`}>
          <ParticipantIcon participant={participant} />
          <span>{participant.name}</span>
        </span>
      ))}
    </div>
  );
}

function InvestigationMomentCard({ moment, participant }: { moment: TourCollaborativeMoment; participant: TourCollaborativeParticipant }) {
  return (
    <li className="r42-investigation-timeline__moment" data-sequence={moment.sequence}>
      <div className="r42-investigation-timeline__marker" aria-hidden="true">
        <span>{moment.sequence}</span>
      </div>

      <article className="r42-investigation-moment-card">
        <header className="r42-investigation-moment-card__header">
          <div className="r42-investigation-moment-person">
            <span
              className="r42-investigation-moment-person__avatar"
              aria-hidden="true"
              data-participant-icon-renderer="svg-v2"
            >
              <ParticipantIcon participant={participant} />
            </span>
            <div>
              <strong>{participant.name}</strong>
              <span>{participant.role}</span>
            </div>
          </div>
          <span className="r42-investigation-moment-card__step">Moment {moment.sequence} of 7</span>
        </header>

        <div className="r42-investigation-moment-card__body">
          <h3>{moment.title}</h3>

          <section className="r42-investigation-moment-block">
            <span className="r42-investigation-moment-label">What {participant.name} added</span>
            <p>{moment.human_contribution}</p>
            <div className="r42-investigation-evidence-chips" aria-label={`Evidence in moment ${moment.sequence}`}>
              {moment.evidence.map((evidence) => (
                <span key={evidence.evidence_id} className="r42-investigation-evidence-chip">
                  <i className={evidenceIcon(evidence.kind)} aria-hidden="true" />
                  {evidence.label}
                </span>
              ))}
            </div>
          </section>

          <section className="r42-investigation-route42-callout">
            <div className="r42-investigation-route42-callout__head">
              <span className="r42-investigation-route42-callout__mark" aria-hidden="true"><i className="bi bi-stars" /></span>
              <strong>How Route42 helped</strong>
            </div>
            <p>{moment.route42_contribution}</p>
          </section>

          <section className="r42-investigation-understanding" aria-label={`Where things stand after moment ${moment.sequence}`}>
            <div className="r42-investigation-understanding__change" aria-hidden="true"><i className="bi bi-arrow-down" /></div>
            <div>
              <span className="r42-investigation-moment-label">Where things stand now</span>
              <p>{moment.understanding_after}</p>
            </div>
          </section>

          <section className="r42-investigation-next-action">
            <i className="bi bi-arrow-right-circle" aria-hidden="true" />
            <div>
              <span>Next useful action</span>
              <p>{moment.next_action}</p>
            </div>
          </section>
        </div>
      </article>
    </li>
  );
}

function ParticipantIcon({ participant }: { participant: TourCollaborativeParticipant }) {
  const commonProps = {
    className: 'r42-investigation-participant-icon',
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    focusable: false,
    'aria-hidden': true,
  };

  if (participant.actor_type === 'AI') {
    return (
      <svg {...commonProps}>
        <path d="M12 4 13.5 8.5 18 10l-4.5 1.5L12 16l-1.5-4.5L6 10l4.5-1.5L12 4Z" />
        <path d="m18.5 14.5.7 2.1 2.1.7-2.1.7-.7 2.1-.7-2.1-2.1-.7 2.1-.7.7-2.1Z" />
      </svg>
    );
  }

  if (participant.role === 'Maintenance Technician') {
    return (
      <svg {...commonProps}>
        <path d="M14.4 6.1a4.35 4.35 0 0 0-5.35 5.35L4.8 15.7a1.9 1.9 0 1 0 2.7 2.7l4.25-4.25a4.35 4.35 0 0 0 5.35-5.35l-2.55 2.55-2.3-.62-.62-2.3 2.77-2.33Z" />
      </svg>
    );
  }

  if (participant.role === 'Reliability Engineer') {
    return (
      <svg {...commonProps}>
        <path d="M5 18h14" />
        <path d="m6.5 15 3.3-3.3 2.8 2.1 4.8-5.3" />
        <path d="M15 8.5h2.5V11" />
      </svg>
    );
  }

  return (
    <svg {...commonProps}>
      <rect x="5" y="5" width="14" height="11" rx="2" />
      <circle cx="12" cy="9.25" r="2" />
      <path d="M8.75 16c.45-1.8 1.55-2.75 3.25-2.75s2.8.95 3.25 2.75" />
      <path d="M9.5 19h5" />
    </svg>
  );
}

function evidenceIcon(kind: TourCollaborativeMoment['evidence'][number]['kind']): string {
  const icons: Record<TourCollaborativeMoment['evidence'][number]['kind'], string> = {
    OBSERVATION: 'bi bi-eye',
    REFERENCE: 'bi bi-file-earmark-text',
    HISTORY: 'bi bi-clock-history',
    MEASUREMENT: 'bi bi-speedometer2',
    ACTION: 'bi bi-wrench',
    OUTCOME: 'bi bi-activity',
    REVIEW: 'bi bi-clipboard-check',
    CLOSURE: 'bi bi-check2-circle',
  };
  return icons[kind];
}
